import React, { useState, useRef, useEffect } from "react";
import "../../styles/cards/card.css";
import "../../styles/dashboard/dashboard.css";
import Modal from "../modal/Modal";
import "../../styles/modal/modal.css";
import PrimaryButton from "../buttons/PrimaryButton";
import adoptionIcon from "../../assets/img/adoption.svg";
import birthIcon from "../../assets/img/birth.svg";
import deathIcon from "../../assets/img/death.svg";
import divorceIcon from "../../assets/img/divorced.svg";
import guardianshipIcon from "../../assets/img/guardianship.svg";
import legitimationIcon from "../../assets/img/legitimization.svg";
import marriageIcon from "../../assets/img/marriage.svg";
import yellowFormIcon from "../../assets/img/forms.svg";
import recognitionIcon from "../../assets/img/recognition.svg";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const InfoCard = ({ title, value, progress, fetchDetails, activeTab }) => {
  const [showProgressToolTip, setShowProgressToolTip] = useState(false);
  const [showToolTip, setShowToolTip] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initialTooltipPosition = { x: 0 };
  const [tooltipPosition, setTooltipPosition] = useState(
    initialTooltipPosition
  );
  const [detailedStats, setDetailedStats] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const progressBarRef = useRef(null);

  const recordTypes = [
    {
      title: "Birth",
      key: "birth",
      icon: <img src={birthIcon} alt="birth icon" width="31px" height="32px"/>,
    },
    {
      title: "Death",
      key: "death",
      icon: <img src={deathIcon} alt="death icon" width="31px" height="32px"/>,
    },
    {
      title: "Divorce",
      key: "divorce",
      icon: <img src={divorceIcon} alt="divorce icon" width="31px" height="32px"/>,
    },
    {
      title: "Marriage",
      key: "marriage",
      icon: <img src={marriageIcon} alt="marriage icon" width="31px" height="32px"/>,
    },
    {
      title: "Recognition",
      key: "recognition",
      icon: <img src={recognitionIcon} alt="recognition icon" width="31px" height="32px"/>,
    },
    {
      title: "Legitimization",
      key: "legitimization",
      icon: <img src={legitimationIcon} alt="legitimation icon" width="31px" height="32px"/>,
    },
    {
      title: "Guardianship",
      key: "guardianship",
      icon: <img src={guardianshipIcon} alt="guardianship icon" width="31px" height="32px"/>,
    },
    {
      title: "Adoption",
      key: "adoption",
      icon: <img src={adoptionIcon} alt="adoption icon" width="31px" height="32px"/>,
    },
  ]

  const yellowFormRecord = [
     {
      title: "Yellow Forms",
      key: "yellowForms",
      icon: <img src={yellowFormIcon} alt="yellow form icon" width="31px" height="32px"/>,
    },
  ]

  useEffect(() => {
    setDetailedStats(null);

    if(showModal && fetchDetails){
      const fetchData = async () => {
        setLoadingDetails(true);
        try {
          const res = await fetchDetails();
          setDetailedStats(res.data);
        } catch (error) {
          console.error("Failed to fetch detailed stats:", error);
        } finally {
          setLoadingDetails(false);
        }
      };
      fetchData();
    }
  }, [activeTab, fetchDetails, showModal]);

  const getRecordTypes = () => {
    if (!detailedStats) {
      if (title === "OVERALL SCANNED YELLOW FORMS" || title === "SCANNED YELLOW FORMS" || title === "OVERALL INDEXED YELLOW FORMS") {
        return yellowFormRecord.map(record => ({ ...record, count: 0 }));
      }
      return recordTypes.map(record => ({ ...record, count: 0 }));
    }

    if (title === "OVERALL SCANNED YELLOW FORMS" || title === "SCANNED YELLOW FORMS" || title === "OVERALL INDEXED YELLOW FORMS") {
      return yellowFormRecord.map(record => ({
        ...record,
        count: detailedStats[record.key]?.records || detailedStats.yellowForms || 0,
      }));
    }

    let statKey;
    if (title === "OVERALL INDEXED RECORDS") statKey = "approved";
    else if (title === "PENDING APPROVAL RECORDS") statKey = "pending";
    else if (title === "DECLINED RECORDS") statKey = "declined";
    else if (title === "OVERALL SCANNED BOOKS") statKey = "books";
    else if (title === "OVERALL SCANNED RECORDS") statKey = "records";
    else if (title === "DECLINED SCANNED RECORDS") statKey = "declinedScanned";

    if(statKey){
      return recordTypes.map(record => ({
        ...record,
        count: detailedStats[record.key]?.[statKey] || 0,
      }));
    }

    return recordTypes.map(record => ({
      ...record,
      count: title === "SCANNED BOOKS"
        ? detailedStats[record.key]?.books || 0
        : detailedStats[record.key]?.records || detailedStats[record.key] || 0,
    }));
  };
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    setTooltipPosition({ x });
  };

  const formatNumber = (num) => {
    const parsedNum = Number(num);
    if (isNaN(parsedNum) || num === undefined || num === null) {
      return "0";
    }
    
    return parsedNum.toLocaleString("en-US");
  };

  // calculate progress
  let currentValue, totalValue, calculatedProgress;
  if (typeof value === "string" && value.includes("/")) {
    [currentValue, totalValue] = value?.split("/").map(Number);
    calculatedProgress =
      totalValue > 0
        ? Number(((currentValue / totalValue) * 100).toFixed(2))
        : 0;
  } else {
    currentValue = value;
    calculatedProgress = progress || 0;
  }

  const formatPercentage = (num) => {
    return Number.isInteger(num) ? num.toString() : num.toFixed(2);
  };

  const renderModalSkeleton = () => {
    return (
      <div className="info-cards-container">
        {recordTypes.map((_, index) => (
          <div key={index} className="record-card">
            <Skeleton circle width={40} height={40} />
            <Skeleton width={60} height={20} style={{ marginTop: "10px" }} />
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (progressBarRef.current) {
      const width = progressBarRef.current.offsetWidth;
      const newPosition = (width * calculatedProgress) / 100;
      setTooltipPosition({ x: newPosition });
    }
  }, [calculatedProgress]);

  const handleTooltipClick = async (e) => {
    e.stopPropagation();
    if(!detailedStats && fetchDetails) {
      setLoadingDetails(true);
      try {
        const res = await fetchDetails();
        setDetailedStats(res.data);
      } catch (error) {
        console.error("Failed to fetch detailed stats:", error);
      } finally {
        setLoadingDetails(false);
      }
    }
    setShowModal(true);
  };

  const hasData = currentValue > 0;
  const hasDetails = title !== "OVERALL SITES SCANNED" && !!fetchDetails;

  return (
    <>
      <div
        className="card"
        onMouseEnter={() => hasData && hasDetails && setShowToolTip(true)}
        onMouseLeave={() => setShowToolTip(false)}
        style={{ position: "relative" }}
      >
        <div className="card-header">
          <h3 className="card-title">{title}</h3>
          <div className="data-value">
            {totalValue ? (
              <>
                <span>{formatNumber(currentValue)}</span>
                <span className="total-value">/{formatNumber(totalValue)}</span>
              </>
            ) : (
              formatNumber(currentValue)
            )}
          </div>
        </div>
        <div className="line"></div>
        <div className="card-content">
          {title !== "SCANNED YELLOW FORMS" && (
            <div className="progress-bar-container">
            <div
              className="progress-bar"
              ref={progressBarRef}
              onMouseMove={handleMouseMove}
              onMouseEnter={() => {
                setShowToolTip(false)
                setShowProgressToolTip(true);}}
              onMouseLeave={() => {
                setShowProgressToolTip(false);
                setShowToolTip(true);
              }}
            >
              <div className="bar-container">
                <div
                  className="bar"
                  style={{
                    width: `${calculatedProgress}%`,
                    backgroundColor: "#078ECE",
                  }}
                ></div>
                {showProgressToolTip && (
                  <div
                  className="tooltip"
                  style={{ left: `${calculatedProgress}%` }}
                >
                  {formatPercentage(calculatedProgress)}%
                </div>
                )}
                
              </div>
            </div>
            <div className="progress-bar-label">Response Rate</div>
          </div>
          )}
          
          {showToolTip && !showProgressToolTip && hasDetails && (
            <div
              className="tooltip center-tooltip"
              onClick={handleTooltipClick}
            >
              Click to view data
            </div>
          )}
        </div>
      </div>

      {hasDetails && (
        <Modal show={showModal} onClose={() => setShowModal(false)} style={{ paddingBlock: "40px"}}>
        {loadingDetails ? (
          renderModalSkeleton()
        ) : (
          <>
            <div className="info-cards-container">
          {getRecordTypes().map((record, index) => (
            <div key={index} className="record-card">
              <div className="record-icon">{record.icon}</div>
              <div className="record-info">
                <div className="record-title">{record.title}</div>
                <div className="record-count">{formatNumber(record.count)}</div>
              </div>
            </div>
          ))}
        </div>
        {/* <div className="modal-footer">
          <PrimaryButton text="Close" onClick={() => setShowModal(false)} />
        </div> */}
          </>
        )}
        
      </Modal>
      )} 
    </>
  );
};

export default InfoCard;
